import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, enrollTotpInit, enrollTotpConfirm } from '../../../hooks/useAuth';
import Stepper from '../../../components/base/Stepper';

type Stage = 'loading' | 'scan' | 'verify' | 'backup-codes';

// Stepper position of each stage: scan the code, confirm it, save the backup codes.
const STAGE_STEP: Record<Stage, number> = { loading: 0, scan: 0, verify: 1, 'backup-codes': 2 };

export default function SetupTwoFactorPage() {
  const { t, i18n } = useTranslation('admin');
  const navigate = useNavigate();
  const { session, clearMustEnrollTotp } = useAuth();
  const token = session?.token ?? null;

  const [stage, setStage] = useState<Stage>('loading');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    enrollTotpInit(token)
      .then((r) => {
        setQr(r.qrDataUrl);
        setSecret(r.base32Secret);
        setSessionId(r.sessionId);
        setStage('scan');
      })
      .catch((e: Error) => {
        setError(e.message);
        setStage('scan');
      });
  }, [token]);

  async function onVerifySubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await enrollTotpConfirm(token!, sessionId, code);
      setBackupCodes(r.backupCodes);
      setStage('backup-codes');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('twoFa.confirmFailed', 'Confirmation failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  function onFinish() {
    if (!acknowledged) return;
    clearMustEnrollTotp();
    navigate('/admin');
  }

  const steps = [
    { id: 'scan', label: t('twoFa.steps.scan', 'Scan the code') },
    { id: 'verify', label: t('twoFa.steps.verify', 'Confirm the code') },
    { id: 'backup', label: t('twoFa.steps.backup', 'Backup codes') },
  ];

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">{t('twoFa.title', 'Set up two-factor authentication')}</h1>
          <p className="text-gray-500 mt-1">{t('twoFa.subtitle', 'Required for admin accounts')}</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Stepper
          steps={steps}
          currentStep={STAGE_STEP[stage]}
          ariaLabel={t('twoFa.steps.label', 'Two-factor setup steps')}
          className="mb-8"
        />

        {error && (
          <div role="alert" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <i className="ri-error-warning-line me-2" aria-hidden="true"></i>
            {error}
          </div>
        )}

        {stage === 'loading' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-gray-600" role="status">
            {t('twoFa.loading', 'Loading…')}
          </div>
        )}

        {stage === 'scan' && qr && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <p className="mb-4 text-gray-700">
              {t('twoFa.scanIntro', 'Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password):')}
            </p>
            <img src={qr} alt={t('twoFa.qrAlt', 'QR code for two-factor setup')} className="mx-auto mb-4" />
            <p className="text-sm text-gray-500 mb-2">{t('twoFa.manualHint', "Can't scan? Enter this code manually:")}</p>
            <code dir="ltr" className="block bg-gray-100 p-3 text-center font-mono mb-4 rounded break-all">{secret}</code>
            <button
              onClick={() => setStage('verify')}
              className="w-full py-3 rounded-xl bg-[#004D40] text-white font-medium hover:bg-[#003930] transition-colors"
            >
              {t('twoFa.added', "I've added it - verify")}
            </button>
          </div>
        )}

        {stage === 'verify' && (
          <form onSubmit={onVerifySubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <p id="twofa-code-hint" className="mb-2 text-gray-700">
              {t('twoFa.verifyIntro', 'Enter the 6-digit code shown in your authenticator app:')}
            </p>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              aria-label={t('twoFa.codeLabel', 'Verification code')}
              aria-describedby="twofa-code-hint"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="block w-48 mx-auto border border-gray-200 rounded-xl p-3 text-center text-2xl tracking-widest mb-4"
            />
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={code.length !== 6 || busy}
                className="flex-1 py-3 rounded-xl bg-[#004D40] text-white font-medium hover:bg-[#003930] disabled:opacity-50 transition-colors"
              >
                {busy ? t('twoFa.verifying', 'Verifying…') : t('twoFa.verify', 'Verify')}
              </button>
              <button
                type="button"
                onClick={() => { setStage('scan'); setCode(''); setError(''); }}
                className="px-4 py-3 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                {t('twoFa.back', 'Back')}
              </button>
            </div>
          </form>
        )}

        {stage === 'backup-codes' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
              <i className="ri-alert-line me-2" aria-hidden="true"></i>
              <strong>{t('twoFa.backupTitle', 'Save these backup codes now.')}</strong>{' '}
              {t('twoFa.backupBody', 'They appear only ONCE. Each one is a single-use replacement for your authenticator app.')}
            </div>
            <ul dir="ltr" className="grid grid-cols-2 gap-2 bg-gray-100 p-4 rounded-xl font-mono text-sm mb-4">
              {backupCodes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <label className="flex items-center mb-4 text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="me-3"
              />
              {t('twoFa.acknowledge', "I've saved these codes in a safe place")}
            </label>
            <button
              onClick={onFinish}
              disabled={!acknowledged}
              className="w-full py-3 rounded-xl bg-[#004D40] text-white font-medium hover:bg-[#003930] disabled:opacity-50 transition-colors"
            >
              {t('twoFa.finish', 'Finish')}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
